/**
 * Helper Functions - Swiss Design
 *
 * Utility functions for combining and applying Swiss style classes
 */

import { SWISS_CARD, SWISS_CARD_STRESS, SWISS_CARD_MOBILE } from './cards';
import { SWISS_BUTTONS, SWISS_BUTTON_MOBILE } from './buttons';
import { SWISS_INPUT_MOBILE } from './inputs';
import { SWISS_STATUS, SWISS_STATUS_MOBILE } from './status';
import { SWISS_MODAL, SWISS_MODAL_STRESS } from './modals';
import { SWISS_EMPHASIS } from './misc';
import { SWISS_LIST_ITEM, SWISS_OPERATION_BADGES } from './lists';
import { SWISS_ALERT_MOBILE } from './misc';

/**
 * Combine multiple Swiss style classes
 * Filters out falsy values for conditional styling
 *
 * @param classes - Array of class names to combine
 * @returns Combined class string
 *
 * @example
 * swissClass(SWISS_CARD.base, SWISS_CARD.hover, isActive && 'bg-blue-50')
 */
export function swissClass(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(' ');
}

/**
 * Create a card with Swiss styling
 *
 * @param interactive - Whether the card should have hover effects
 * @param className - Additional custom classes
 * @returns Combined card class string
 */
export function swissCard(interactive = false, className?: string): string {
  return swissClass(
    interactive ? SWISS_CARD.interactive : SWISS_CARD.base,
    className
  );
}

/**
 * Create a button with Swiss styling
 *
 * @param variant - Button variant
 * @param className - Additional custom classes
 * @returns Combined button class string
 */
export function swissButton(
  variant: keyof typeof SWISS_BUTTONS = 'primary',
  className?: string
): string {
  return swissClass(SWISS_BUTTONS[variant], className);
}

/**
 * Create a status badge with Swiss styling
 *
 * @param status - Status type
 * @param className - Additional custom classes
 * @returns Combined status class string
 */
export function swissStatus(
  status: keyof typeof SWISS_STATUS = 'neutral',
  className?: string
): string {
  return swissClass(SWISS_STATUS[status], 'border', className);
}

/**
 * Create a stress-optimized card with Swiss styling
 *
 * @param variant - Card variant (base, interactive, emphasized, glass)
 * @param className - Additional custom classes
 * @returns Combined stress card class string
 */
export function swissCardStress(
  variant: keyof typeof SWISS_CARD_STRESS = 'base',
  className?: string
): string {
  return swissClass(SWISS_CARD_STRESS[variant], className);
}

/**
 * Create a modal component with Swiss styling
 *
 * @param part - Modal part (overlay, content, fullscreen, header, body, footer)
 * @param className - Additional custom classes
 * @returns Combined modal class string
 */
export function swissModal(
  part: keyof typeof SWISS_MODAL = 'content',
  className?: string
): string {
  return swissClass(SWISS_MODAL[part], className);
}

/**
 * Apply emphasis styling with functional colors
 *
 * @param type - Emphasis type (critical, warning, success, info, actionPrimary, etc.)
 * @param className - Additional custom classes
 * @returns Combined emphasis class string
 */
export function swissEmphasis(
  type: keyof typeof SWISS_EMPHASIS = 'info',
  className?: string
): string {
  return swissClass(SWISS_EMPHASIS[type], className);
}

/**
 * Get operation badge styling
 *
 * @param type - Operation type (prodotto, rete, rete_reso, biglietto, annullato)
 * @param className - Additional custom classes
 * @returns Combined badge class string
 */
export function swissOperationBadge(
  type: keyof typeof SWISS_OPERATION_BADGES = 'default',
  className?: string
): string {
  return swissClass(
    SWISS_LIST_ITEM.badge,
    SWISS_OPERATION_BADGES[type] || SWISS_OPERATION_BADGES.default,
    className
  );
}

// ============================================================================
// MOBILE HELPER FUNCTIONS
// ============================================================================

/**
 * Create a mobile-optimized card
 */
export function swissCardMobile(
  variant: keyof typeof SWISS_CARD_MOBILE = 'base',
  className?: string
): string {
  return swissClass(SWISS_CARD_MOBILE[variant], className);
}

/**
 * Create a mobile button with Swiss styling
 */
export function swissButtonMobile(
  variant: keyof typeof SWISS_BUTTON_MOBILE = 'primary',
  className?: string
): string {
  return swissClass(SWISS_BUTTON_MOBILE[variant], className);
}

/**
 * Create mobile-optimized input
 */
export function swissInputMobile(
  variant: keyof typeof SWISS_INPUT_MOBILE = 'base',
  className?: string
): string {
  return swissClass(SWISS_INPUT_MOBILE[variant], className);
}

/**
 * Create mobile alert
 */
export function swissAlertMobile(
  variant: keyof typeof SWISS_ALERT_MOBILE = 'info',
  className?: string
): string {
  return swissClass(SWISS_ALERT_MOBILE.base, SWISS_ALERT_MOBILE[variant], className);
}

/**
 * Create mobile status badge
 */
export function swissStatusMobile(
  status: keyof typeof SWISS_STATUS_MOBILE = 'neutral',
  className?: string
): string {
  return swissClass(SWISS_STATUS_MOBILE[status], className);
}
